import React, { useState } from 'react';
import { Loader2, X, CheckCheck, FileDown, Mail, UserCheck, ClipboardList, Layers, TrendingUp } from 'lucide-react';

const STATUS_OPTIONS = [
  'pending', 'paid', 'failed', 'in_production', 'packed',
  'dispatched', 'delivered', 'completed', 'refunded', 'partially_refunded',
];

// Floating bulk-action bar shown when one or more orders are selected.
// Supports bulk status change, bulk Sage export, bulk status-email send, and
// bulk assign-to-consumer-account of the selected orders.
export default function AdminOrdersBulkBar({ selectedCount, allSelectedOnPage, onSelectAllOnPage, onClear, onBulkStatus, onBulkSageExport, onBulkSendStatusEmail, onBulkPickingList, onAssignAccount, onBulkMerge, onBulkMarginExport, busy }) {
  const [status, setStatus] = useState('');

  if (selectedCount === 0) return null;

  return (
    <div className="sticky bottom-0 z-30 bg-[#111115] border-t border-primary/30 px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-3">
        <span className="bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-full">{selectedCount} selected</span>
        <button onClick={onSelectAllOnPage} className="text-white/60 hover:text-white text-xs font-bold tracking-wider flex items-center gap-1.5">
          <CheckCheck className="w-3.5 h-3.5" /> {allSelectedOnPage ? 'UNSELECT PAGE' : 'SELECT PAGE'}
        </button>
        <button onClick={onClear} className="text-white/40 hover:text-white text-xs font-bold tracking-wider flex items-center gap-1.5">
          <X className="w-3.5 h-3.5" /> CLEAR
        </button>
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-[#0d0d0f] border border-white/10 text-white text-xs font-bold h-9 px-3 focus:border-primary/60 outline-none"
        >
          <option value="">Set status…</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s} className="bg-[#0d0d0f]">{s.replace(/_/g, ' ').toUpperCase()}</option>
          ))}
        </select>
        <button
          onClick={() => status && onBulkStatus(status)}
          disabled={!status || busy}
          className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} APPLY
        </button>
        <button
          onClick={onBulkSageExport}
          disabled={busy}
          title="Export the selected orders to a Sage Audit Trail CSV"
          className="flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} SAGE EXPORT
        </button>
        <button
          onClick={onBulkPickingList}
          disabled={busy}
          title="Generate one picking list PDF covering the selected orders"
          className="flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />} PICKING LIST
        </button>
        <button
          onClick={onBulkMarginExport}
          disabled={busy}
          title="Export the selected sales' COGS and gross margin as a CSV"
          className="flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />} MARGIN CSV
        </button>
        <button
          onClick={onBulkSendStatusEmail}
          disabled={busy}
          title="Send the current status email to the selected customers"
          className="flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} SEND STATUS EMAIL
        </button>
        <button
          onClick={onAssignAccount}
          disabled={busy}
          title="Assign the selected orders to a consumer account"
          className="flex items-center gap-1.5 border border-white/10 hover:border-primary/50 text-white/70 hover:text-white font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCheck className="w-3.5 h-3.5" />} ASSIGN ACCOUNT
        </button>
        <button
          onClick={onBulkMerge}
          disabled={busy || selectedCount < 2}
          title="Combine these records into one order with a single postage and one invoice (use for a basket that was split into several orders)"
          className="flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary font-bold text-xs tracking-wider px-4 h-9 disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />} MERGE ORDERS
        </button>
      </div>
    </div>
  );
}